import React, { useEffect, useState } from 'react';
import { Sparkles, Clock, Lightbulb } from 'lucide-react';
import OptionSheet from './OptionSheet';
import { supabase } from '../../lib/supabase';
import { postPromptInstrucciones } from '../../lib/promptInstructions';

const escenaExamplesMap: Record<string, string[]> = {
  'Moda e Indumentaria': [
    'En una calle empedrada de una ciudad europea al atardecer',
    'En un estudio fotográfico con fondo de tela beige',
    'En una cafetería moderna con luz natural entrando por ventanales',
    'En un parque con vegetación verde y luz suave de la mañana',
  ],
  'Hogar y Decoración': [
    'En un living minimalista con luz natural y paredes blancas',
    'En una habitación con estilo nórdico y plantas decorativas',
    'En una terraza con vista al jardín durante la hora dorada',
    'En una cocina moderna con mesada de mármol blanco',
  ],
  'Belleza y Cuidado Personal': [
    'Sobre una superficie de mármol con pétalos de flores alrededor',
    'En un baño elegante con toallas blancas y velas aromáticas',
    'Sobre una mesa de madera clara con plantas verdes de fondo',
    'En un tocador con espejo dorado y luz cálida',
  ],
  'Tecnología': [
    'En un escritorio minimalista con fondo oscuro y luces LED',
    'En un espacio de coworking moderno con buena iluminación',
    'Sobre una superficie reflectante con fondo degradado azul',
    'En una mesa blanca con setup tecnológico de fondo',
  ],
  'Salud y Bienestar': [
    'En un espacio zen con bambú y piedras naturales',
    'En un ambiente claro y sereno con plantas medicinales',
    'Sobre una superficie de madera con ingredientes naturales alrededor',
    'En un jardín tranquilo con luz filtrada entre las hojas',
  ],
  'Automotriz': [
    'En una ruta panorámica al atardecer con montañas de fondo',
    'En un garaje moderno con piso de concreto pulido',
    'En una calle urbana de noche con reflejos de luces de neón',
    'En un paisaje costero con el mar de fondo',
  ],
  'Gastronomía': [
    'En una mesa rústica de madera con ingredientes frescos alrededor',
    'En una cocina profesional con fondo de acero inoxidable',
    'En un restaurante elegante con luz de velas',
    'En una tabla de madera con hierbas frescas y especias',
  ],
  'Juguetería': [
    'En una habitación infantil colorida con estantes de fondo',
    'En un parque de juegos con césped verde y cielo azul',
    'Sobre una alfombra suave con cubos de colores alrededor',
    'En un espacio luminoso con globos y decoraciones festivas',
  ],
  'Arte y Manualidades': [
    'En un taller de arte con pinturas y pinceles de fondo',
    'Sobre una mesa de madera con materiales creativos alrededor',
    'En un espacio bohemio con telas coloridas y buena luz natural',
    'En un estudio con paredes de ladrillo y obras de arte',
  ],
  'Librería': [
    'En una biblioteca acogedora con estantes llenos de libros',
    'Junto a una ventana con luz natural y una taza de café',
    'En un rincón de lectura con sillón y lámpara cálida',
    'Sobre una mesa de madera oscura con hojas otoñales',
  ],
  'Creación de Contenido': [
    'En un set de filmación con luces profesionales de fondo',
    'En un escritorio de creador con micrófono y pantallas',
    'En un espacio moderno con neón y decoración urbana',
    'En un estudio con fondo degradado y ring light',
  ],
};
const fallbackExamples = [
  'En un estudio fotográfico con fondo blanco y luz suave',
  'Sobre una mesa de mármol con plantas verdes de fondo',
  'En un espacio moderno con buena iluminación natural',
  'En una superficie de madera con fondo desenfocado elegante',
];

interface EscenaPersonalizadaOptionProps {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (v: string) => void;
  onAccept: (v: string) => void;
  ideas?: string[];
  variantIds?: string[];
}

const EscenaPersonalizadaOption: React.FC<EscenaPersonalizadaOptionProps> = ({
  open,
  onClose,
  value,
  onChange,
  onAccept,
  ideas = [],
  variantIds = [],
}) => {
  const [local, setLocal] = useState(value);
  const [historial, setHistorial] = useState<string[]>([]);
  const [rubro, setRubro] = useState<string>('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [mejorando, setMejorando] = useState(false);
  const [generandoIdeas, setGenerandoIdeas] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(4);
  const [generatedIdeas, setGeneratedIdeas] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setLocal(value);
      setVisibleCount(4);
      setGeneratedIdeas([]);
      fetchContext();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContext = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
      const idCompany = profile?.id_company;
      if (!idCompany) return;
      setCompanyId(idCompany);

      const [companyRes, historialRes] = await Promise.all([
        supabase.from('company_profiles').select('rubro').eq('id_company', idCompany).single(),
        supabase.from('historial_prompts').select('prompt').eq('id_company', idCompany).eq('tipo', 'escena').order('created_at', { ascending: false }).limit(10),
      ]);
      if (companyRes.data?.rubro) setRubro(companyRes.data.rubro as string);
      if (historialRes.data) {
        const unique = [...new Set(historialRes.data.map((d: any) => d.prompt as string))];
        setHistorial(unique);
      }
    } catch {}
  };

  const handleMejorar = async () => {
    if (mejorando || !local.trim()) return;
    try {
      setMejorando(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const vIds = variantIds.filter(Boolean);
      if (vIds.length === 0) return;
      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: vIds,
        modo: 'mejorar',
        prompt: local.trim(),
        tipo_de_prompt: 'escena',
      });
      if (data?.prompt_mejorado) setLocal(data.prompt_mejorado);
    } catch (e) {
      console.error(e);
    } finally {
      setMejorando(false);
    }
  };

  const handleGenerateIdeas = async () => {
    if (generandoIdeas) return;
    try {
      setGenerandoIdeas(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const vIds = variantIds.filter(Boolean);
      if (vIds.length === 0) return;
      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: vIds,
        modo: 'generar_ideas',
        tipo_de_prompt: 'escena',
      });
      if (Array.isArray(data?.posts)) {
        const newIdeas = data.posts
          .filter((p: any) => p?.titulo || p?.prompt)
          .map((p: any) => (p.prompt || p.titulo));
        if (newIdeas.length > 0) setGeneratedIdeas(prev => [...prev, ...newIdeas]);
      } else if (Array.isArray(data?.ideas)) {
        setGeneratedIdeas(prev => [...prev, ...data.ideas]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setGenerandoIdeas(false);
    }
  };

  const examples = escenaExamplesMap[rubro] || fallbackExamples;

  const content = (
    <div className="flex flex-col flex-1">
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <textarea
          value={local}
          onChange={(e) => {
            setLocal(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          placeholder="Describí la escena que querés para tus imágenes"
          className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px]"
        />
        <div 
          className="-mx-4 mt-3 pt-3 px-4 border-t border-black/[0.06] flex justify-start transition-all duration-300 ease-out"
          style={{ opacity: local.trim() ? 1 : 0, maxHeight: local.trim() ? 48 : 0, overflow: 'hidden', pointerEvents: local.trim() ? 'auto' : 'none' }}
        >
            <button type="button" onClick={handleMejorar} disabled={mejorando} className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50 disabled:cursor-not-allowed overflow-hidden">
              <Sparkles className={`w-4 h-4 text-[#1644EB] ${mejorando ? 'animate-pulse' : ''}`} />
              <span>{mejorando ? 'Mejorando...' : 'Mejorar'}</span>
              {mejorando && (
                <>
                  <span className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]" style={{ animation: 'widthExpand 25s linear forwards' }} />
                  <style>{`@keyframes widthExpand { from { width: 0%; } to { width: 100%; } }`}</style>
                </>
              )}
            </button>
        </div>
      </div>

      <div className="mt-4">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">{historial.length > 0 ? 'Utilizado anteriormente' : 'Ejemplos'}</p>
          {(() => {
            const isHist = historial.length > 0;
            const allItems = isHist ? historial : examples;
            const visibleItems = allItems.slice(0, visibleCount);
            const hasMore = allItems.length > visibleCount;
            return (
              <>
                <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                  {visibleItems.map((item, i) => (
                    <button key={i} type="button" onClick={() => setLocal(item)} className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < visibleItems.length - 1 || hasMore ? 'border-b border-black/[0.06]' : ''}`}>
                      {isHist ? <Clock className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" /> : <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />}
                      <span className="line-clamp-3">{item}</span>
                    </button>
                  ))}
                  {hasMore && (
                    <button type="button" onClick={() => setVisibleCount(prev => prev + 3)} className="w-full text-left px-4 py-3 text-[15px] text-brand-primary/80 active:bg-neutral-100 transition-colors">
                      Ver más
                    </button>
                  )}
                </div>
              </>
            );
          })()}

          {generatedIdeas.length > 0 && (
            <div className="mt-4">
              <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Ideas generadas</p>
              <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                {generatedIdeas.map((item, i) => (
                  <button key={i} type="button" onClick={() => setLocal(item)} className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < generatedIdeas.length - 1 ? 'border-b border-black/[0.06]' : ''}`}>
                    <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                    <span className="line-clamp-3">{item}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button type="button" onClick={handleGenerateIdeas} disabled={generandoIdeas} className="relative mt-3 w-full text-left px-4 py-3 text-[15px] text-brand-primary rounded-[13px] bg-white border border-neutral-200 active:bg-neutral-100 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden">
            <Sparkles className={`w-4 h-4 ${generandoIdeas ? 'animate-pulse' : ''}`} />
            <span>{generandoIdeas ? 'Generando...' : 'Generar más ideas'}</span>
            {generandoIdeas && (
              <>
                <span className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]" style={{ animation: 'widthExpand 25s linear forwards' }} />
                <style>{`@keyframes widthExpand { from { width: 0%; } to { width: 100%; } }`}</style>
              </>
            )}
          </button>
      </div>
    </div>
  );

  return (
    <OptionSheet
      open={open}
      onClose={() => {
        setLocal(value);
        onClose();
      }}
      onAccept={() => {
        onChange(local);
        onAccept(local);
      }}
      title="Escena personalizada"
      acceptDisabled={!local.trim()}
    >
      {content}
    </OptionSheet>
  );
};

export default EscenaPersonalizadaOption;
